import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Phone } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { LeadForm } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { SERVICES_SEO, CITIES } from "@/data/servicePages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

function setMetaDescription(content) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export default function ServiceCityPage() {
  const { serviceSlug, citySlug } = useParams();
  const service = SERVICES_SEO.find((s) => s.slug === serviceSlug);
  const city = CITIES.find((c) => c.slug === citySlug);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    if (service && city) {
      document.title = `${service.name} in ${city.name}, OR | Exotictileandmasonry`;
      setMetaDescription(
        `${service.name} in ${city.name}, Oregon by Exotictileandmasonry. ${
          service.offer || service.bullets[0]
        } Call 971-255-3874 for a free quote.`
      );
    }
    return () => {
      document.title =
        "Exotictileandmasonry | $249 Chimney Sweep + Free Inspection — Greater PDX";
    };
  }, [service, city]);

  useEffect(() => {
    if (!service || !city) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-service";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: service.name,
      name: `${service.name} in ${city.name}, OR`,
      description: service.intro(city.name),
      provider: {
        "@type": "ChimneySweep",
        name: "Exotictileandmasonry",
        telephone: "+1-971-255-3874",
        url: "https://pdx-chimney-pro.emergent.host",
      },
      areaServed: { "@type": "City", name: `${city.name}, OR` },
      ...(service.slug === "chimney-sweeping" && {
        offers: {
          "@type": "Offer",
          name: "Chimney Sweeping with Free Inspection",
          price: "249",
          priceCurrency: "USD",
        },
      }),
    });
    document.head.appendChild(script);
    return () => document.getElementById("ld-service")?.remove();
  }, [service, city]);

  if (!service || !city) return <Navigate to="/" replace />;

  const photo = photos.find((p) => p.title === service.photoTitle);

  return (
    <div data-testid="service-city-page">
      <Header />
      <main className="pt-16">
        <section className="bg-accent py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              data-testid="back-home-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to home
            </Link>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <motion.div
                className="lg:col-span-7"
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                  {city.name}, Oregon
                </p>
                <h1
                  data-testid="page-title"
                  className="font-heading text-4xl sm:text-5xl tracking-tighter font-extrabold text-white leading-tight"
                >
                  {service.name} in {city.name}, OR
                </h1>
                {service.offer && (
                  <span
                    data-testid="page-offer-badge"
                    className="inline-block mt-5 bg-primary text-white text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 rounded-md"
                  >
                    {service.offer}
                  </span>
                )}
                <p className="text-base md:text-lg text-white/75 leading-relaxed mt-6 max-w-xl">
                  {service.intro(city.name)} {city.blurb}
                </p>
                <ul className="mt-8 space-y-3" data-testid="page-bullets">
                  {service.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/85">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span className="text-sm font-semibold">{b}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  data-testid="page-call-button"
                  className="btn-press mt-10 bg-primary hover:bg-[#A93A24] text-white font-bold rounded-md h-12 px-8"
                >
                  <a href="tel:+19712553874" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" strokeWidth={2} /> Call 971-255-3874
                  </a>
                </Button>
              </motion.div>
              {photo && (
                <motion.div
                  className="lg:col-span-5"
                  initial={{ opacity: 0, y: 48 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
                >
                  <div className="bg-card border border-white/10 rounded-md overflow-hidden shadow-2xl">
                    <img
                      src={`${API}/files/${photo.storage_path}`}
                      alt={`${service.name} before and after in ${city.name}, OR`}
                      className="w-full h-auto block"
                      data-testid="page-service-photo"
                    />
                    {photo.description && (
                      <p className="text-xs text-muted-foreground p-4">{photo.description}</p>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-muted py-16" data-testid="keyword-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-foreground mb-4">
              Trusted {service.name} Across {city.name}
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed max-w-3xl">
              {service.keywordPara(city.name)}
            </p>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mt-8 mb-3">
              Neighborhoods We Serve in {city.name}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="neighborhood-chips">
              {city.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="bg-white border border-border text-sm font-semibold text-foreground px-3 py-1.5 rounded-md"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                Free Quote
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-foreground">
                Get Your Free {service.name} Quote in {city.name}
              </h2>
              <p className="text-base text-muted-foreground mt-4 leading-relaxed max-w-md">
                Send your name and number and we'll call you back with a straight answer
                and a fair price. Most quotes are given same-day.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <span className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-sm text-muted-foreground">Prefer to talk?</p>
                  <a
                    href="tel:+19712553874"
                    data-testid="page-call-link"
                    className="font-heading font-bold text-lg text-foreground hover:text-primary transition-colors"
                  >
                    971-255-3874
                  </a>
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-md p-8" data-testid="page-quote-form-card">
              <LeadForm variant="quote" defaultService={service.leadService} />
            </div>
          </div>
        </section>

        <section className="pb-24" data-testid="cross-links-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-4">
                {service.name} in Other Areas
              </h3>
              <ul className="space-y-2">
                {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/services/${service.slug}/${c.slug}`}
                      data-testid={`cross-city-link-${c.slug}`}
                      className="text-sm text-primary font-semibold hover:underline"
                    >
                      {service.name} in {c.name}, OR
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-4">
                Other Services in {city.name}
              </h3>
              <ul className="space-y-2">
                {SERVICES_SEO.filter((s) => s.slug !== service.slug).map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}/${city.slug}`}
                      data-testid={`cross-service-link-${s.slug}`}
                      className="text-sm text-primary font-semibold hover:underline"
                    >
                      {s.name} in {city.name}, OR
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallBar />
    </div>
  );
}
